import { Alert, Button, Card, Form, Input, InputNumber, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSectionSelect,
  EducationSessionSelect,
  EducationSubjectSelect,
} from '@/pages/education/components/EducationLookupSelects';

type RuleSet = { id: string; name: string; session_id: string; status: string; version_no: number };
type SetupValidation = {
  ready: boolean;
  missing: string[];
  summary: Record<string, number | string>;
};

export default function SmartTimetableSetupPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sessionId, setSessionId] = useState<string>();
  const [selectedRuleSetId, setSelectedRuleSetId] = useState<string>();
  const [createForm] = Form.useForm();
  const [reqForm] = Form.useForm();
  const [genForm] = Form.useForm();

  const ruleSets = useQuery({
    queryKey: ['education-smart-tt-rule-sets', sessionId],
    queryFn: () =>
      api.get<RuleSet[]>('/education/timetable/smart/rule-sets', {
        params: sessionId ? { session_id: sessionId } : undefined,
      }),
  });
  const rows = Array.isArray(ruleSets.data?.data) ? ruleSets.data!.data : [];

  const setup = useQuery({
    queryKey: ['education-smart-tt-setup', selectedRuleSetId],
    enabled: Boolean(selectedRuleSetId),
    queryFn: () =>
      api.get<SetupValidation>(`/education/timetable/smart/rule-sets/${selectedRuleSetId}/setup-validation`),
  });

  const createRuleSet = useMutation({
    mutationFn: (values: Record<string, string>) => api.post('/education/timetable/smart/rule-sets', values),
    onSuccess: (result) => {
      message.success(t('saved', { defaultValue: 'Saved' }));
      setSelectedRuleSetId(result.data?.id);
      queryClient.invalidateQueries({ queryKey: ['education-smart-tt-rule-sets'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addRequirement = useMutation({
    mutationFn: (values: Record<string, string | number>) =>
      api.post(`/education/timetable/smart/rule-sets/${selectedRuleSetId}/requirements`, values),
    onSuccess: () => {
      message.success(t('saved', { defaultValue: 'Saved' }));
      queryClient.invalidateQueries({ queryKey: ['education-smart-tt-setup', selectedRuleSetId] });
      reqForm.resetFields();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const generate = useMutation({
    mutationFn: (values: Record<string, string | number>) =>
      api.post<{ id: string }>('/education/timetable/smart/generate', {
        ...values,
        rule_set_id: selectedRuleSetId,
      }),
    onSuccess: (result) => {
      message.success(t('generationQueued', { defaultValue: 'Generation queued' }));
      navigate(`/education/timetable/smart/runs/${result.data?.id}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('schedulingSetup', { defaultValue: 'Scheduling setup' })}
      description={t('schedulingSetupIntro', {
        defaultValue: 'Configure scope, subject requirements and generation options before running CP-SAT.',
      })}
      extra={<Button onClick={() => navigate('/education/timetable/smart')}>{t('back', { defaultValue: 'Back' })}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={t('scope', { defaultValue: 'Scope' })}>
          <EducationSessionSelect
            value={sessionId}
            style={{ minWidth: 280 }}
            onChange={(value) => setSessionId(typeof value === 'string' ? value : undefined)}
          />
        </Card>

        <Card size="small" title={t('ruleSets', { defaultValue: 'Rule sets' })}>
          <Form
            form={createForm}
            layout="inline"
            onFinish={(values) => createRuleSet.mutate({ ...values, session_id: sessionId })}
            style={{ marginBottom: 16 }}
          >
            <Form.Item name="name" rules={[{ required: true }]}>
              <Input placeholder={t('name')} style={{ width: 220 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={createRuleSet.isPending} disabled={!sessionId}>
              {t('createRuleSet', { defaultValue: 'Create rule set' })}
            </Button>
          </Form>
          <Table
            rowKey="id"
            size="small"
            loading={ruleSets.isLoading}
            dataSource={rows}
            pagination={false}
            onRow={(row) => ({
              onClick: () => setSelectedRuleSetId(row.id),
              style: { cursor: 'pointer', background: row.id === selectedRuleSetId ? '#f5f5f5' : undefined },
            })}
            columns={[
              { title: t('name'), dataIndex: 'name' },
              { title: t('status'), dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
              { title: t('version', { defaultValue: 'Version' }), dataIndex: 'version_no', width: 90 },
            ]}
          />
        </Card>

        {selectedRuleSetId ? (
          <>
            <Card size="small" title={t('subjectRequirements', { defaultValue: 'Subject requirements' })}>
              <Form form={reqForm} layout="inline" onFinish={(values) => addRequirement.mutate(values)}>
                <Form.Item name="section_id" rules={[{ required: true }]}>
                  <EducationSectionSelect style={{ minWidth: 200 }} />
                </Form.Item>
                <Form.Item name="subject_id" rules={[{ required: true }]}>
                  <EducationSubjectSelect style={{ minWidth: 200 }} />
                </Form.Item>
                <Form.Item name="periods_per_week" rules={[{ required: true }]} initialValue={4}>
                  <InputNumber min={1} max={20} placeholder="Periods/week" />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={addRequirement.isPending}>
                  {t('add', { defaultValue: 'Add' })}
                </Button>
              </Form>
            </Card>

            <Card size="small" title={t('setupValidation', { defaultValue: 'Setup validation' })} loading={setup.isLoading}>
              {setup.data?.data?.ready ? (
                <Alert type="success" showIcon message={t('readyToGenerate', { defaultValue: 'Ready to generate' })} />
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message={t('setupIncomplete', { defaultValue: 'Setup incomplete' })}
                  description={(setup.data?.data?.missing || []).join(' · ') || '—'}
                />
              )}
            </Card>

            <Card size="small" title={t('generate', { defaultValue: 'Generate' })}>
              <Form
                form={genForm}
                layout="vertical"
                style={{ maxWidth: 420 }}
                initialValues={{ time_budget_sec: 30, candidate_count: 2 }}
                onFinish={(values) => generate.mutate(values)}
              >
                <Form.Item name="time_budget_sec" label={t('timeBudgetSec', { defaultValue: 'Time budget (sec)' })}>
                  <InputNumber min={5} max={300} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="candidate_count" label={t('candidateCount', { defaultValue: 'Candidates' })}>
                  <InputNumber min={1} max={5} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="scenario_name_prefix" label={t('scenarioPrefix', { defaultValue: 'Scenario name prefix' })}>
                  <Input />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={generate.isPending} disabled={!setup.data?.data?.ready}>
                  {t('runGeneration', { defaultValue: 'Run generation' })}
                </Button>
              </Form>
            </Card>
          </>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
